import { Typography } from '@material-ui/core';
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import { MessageBox } from 'design-system';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

// Models
import PersonValidationResponse from 'src/models/responseTypes/PersonValidationResponse';

// Functions
import { checkIdFormat } from 'src/functions/identificationMethods';

export const instructionsLabel: React.ReactElement = (
  <Typography id="sales-web-inspection-label-instructions" variant="caption">
    Sin espacios ni guiones (ej. 888888888), tal como aparece en tu
    identificación.
  </Typography>
);

export const loadingLabel: React.ReactElement = (
  <Typography id="sales-web-inspection-label-loading" variant="caption">
    Validando usuario...
  </Typography>
);

export const formatErrorTextBox: React.ReactElement = (
  <MessageBox
    hideCloseButton
    variant="error"
    message={
      <div id="sales-web-inspection-label-incorrect-format">
        Su identificación no está en el formato correcto.
      </div>
    }
  />
);

export type UsePersonPropertyValidatorProps = {
  personData: PersonValidationResponse;
  identificationNumber: string;
  identificationType: string;
  personSearchRequestStatus: RequestIndicatorTypes;
  onPersonSearchRequested: (identification: string, idType: string) => void;
};

export type DebounceSearchPersonType = (
  param: string,
  type: string,
  validationError: string,
) => void;

export default function usePersonPropertyValidator({
  personData,
  identificationNumber,
  identificationType,
  personSearchRequestStatus,
  onPersonSearchRequested,
}: UsePersonPropertyValidatorProps): {
  personIsInvalid: boolean;
  messageLabel: React.ReactElement;
  formatError: string;
  idCheckRequested: boolean;
  formatDimexError: boolean;
} {
  const [formatError, updateFormatError] = useState('');
  const [formatDimexError, setFormatDimexError] = useState(false);
  const [previousId, updatePreviousId] = useState('');
  const [invalidPersonId, toggleInvalidPersonId] = useState(false);
  const [messageLabel, updateMessageLabel] =
    useState<React.ReactElement>(instructionsLabel);
  const [idCheckRequested, toggleIdCheckRequested] = useState(false);

  /**
   * Maintain a reference to the debounced search method. Note that a debounced method
   * will only fire after not being invoked for a certain amount of time (in this case,
   * 1.8 seconds).
   */
  const debouncedSearchPerson = useRef(
    debounce<DebounceSearchPersonType>((query, idType, validationError) => {
      if (query.length >= 3) {
        updatePreviousId(`${query}${idType}`);
        if (validationError) {
          toggleInvalidPersonId(true);
        } else {
          toggleInvalidPersonId(false);
          onPersonSearchRequested(query, idType);
          toggleIdCheckRequested(true);
        }
      }
    }, 1800),
  ).current;

  /**
   * Update the validation error
   */
  useEffect(() => {
    updateFormatError(checkIdFormat(identificationNumber, identificationType));
  }, [identificationNumber, identificationType]);

  /**
   * Call the debounced search method each time one of these values change.
   */
  useEffect(() => {
    debouncedSearchPerson(
      identificationNumber,
      identificationType,
      formatError,
    );
  }, [
    debouncedSearchPerson,
    identificationNumber,
    identificationType,
    formatError,
  ]);

  /**
   * This effect will manage the message label display logic. The logic is as follows:
   * - If a validation error occurs, display the validation error message.
   * - If the person doesn't exists, display a generic welcome.
   * - If the person exists and the data matches, display a "welcome back" message
   * - If the person exists, but the data doesn't match, display a mismatch error message.
   */
  useEffect(() => {
    setFormatDimexError(false);
    if (previousId !== `${identificationNumber}${identificationType}`) {
      return;
    }
    if (personSearchRequestStatus === 'requesting') {
      updateMessageLabel(loadingLabel);
      return;
    }
    if (identificationType === 'U' && formatError) {
      setFormatDimexError(true);
      return;
    }
    if (formatError) {
      updateMessageLabel(formatErrorTextBox);
      return;
    }
    updateMessageLabel(instructionsLabel);
  }, [
    identificationNumber,
    personSearchRequestStatus,
    formatError,
    updateMessageLabel,
    previousId,
    identificationType,
    formatDimexError,
  ]);

  /**
   * We just need to expose personIsValid and messageLabel. Everything else is considered
   * "internal" implementation and should not be exposed to the rest of the application.
   */
  return {
    personIsInvalid: invalidPersonId || !personData.isValidPerson,
    messageLabel,
    formatError,
    idCheckRequested,
    formatDimexError,
  };
}
